import { formatDateTime, timeAgo } from '@/lib/format';
import { stagger } from '@/lib/motion';
import { useNow } from '@/lib/useNow';
import { cn } from '@/lib/utils';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { StatusPill } from '@/components/badges';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useSeesProviders } from '@/lib/useAudience';
import { describeEvent, type LiveEvent, type StreamStatus } from './liveModel';
import { useLiveStream } from './useLiveStream';

const STATUS_LABEL: Record<StreamStatus, string> = { connecting: 'connecting…', connected: 'connected', reconnecting: 'reconnecting…' };

const DOT_TONE: Record<StreamStatus, string> = { connected: 'pulse text-running', reconnecting: 'text-awaiting', connecting: 'text-subtle' };

function StatusDot({ status }: { status: StreamStatus }) {
  return <span data-testid="live-status" data-status={status} title={STATUS_LABEL[status]} className={cn('inline-block size-2 shrink-0 rounded-full bg-current', DOT_TONE[status])} />;
}

function LiveHeader({ status, count }: { status: StreamStatus; count: number }) {
  return (
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <StatusDot status={status} />
        <Kicker>Live activity</Kicker>
      </CardTitle>
      <CardAction className="font-mono text-xs text-subtle">
        <span role="status">{STATUS_LABEL[status]}</span>
        <span aria-hidden>·</span>
        <span className="tabular">{count} events</span>
      </CardAction>
    </CardHeader>
  );
}

function NoEvents({ status, error }: { status: StreamStatus; error: string | null }) {
  const reconnecting = status === 'reconnecting';
  return (
    <EmptyState
      compact
      icon="hub"
      title={reconnecting ? 'Reconnecting to the event stream' : 'Listening for events'}
      description={reconnecting && error ? `${error} — retrying with backoff.` : 'Webhooks and builds will appear here as they happen.'}
    />
  );
}

/** Screen 01 "Live activity" (P1: provider webhooks and, later, builds). */
export function LiveActivity({ className }: { className?: string }) {
  const { status, events, error } = useLiveStream(['integrations', 'builds']);
  const now = useNow(10_000);

  return (
    <Card className={cn('enter', className)} style={stagger(5)}>
      <LiveHeader status={status} count={events.length} />
      <CardContent className="p-3 sm:p-4">
        {events.length === 0 ? (
          <NoEvents status={status} error={error} />
        ) : (
          <ul className="flex flex-col gap-2">
            {events.map((ev, i) => (
              <EventRow key={ev.id} ev={ev} now={now} index={Math.min(i, 8)} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function Dot() {
  return (
    <span aria-hidden className="text-subtle max-sm:hidden">
      ·
    </span>
  );
}

/** One line from tablet up ("· " separators); on phones the title sits alone and slug + status wrap below it. */
function EventText({ title, slug, status }: { title: string; slug?: string; status?: string }) {
  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
      <span className="font-medium text-foreground max-sm:basis-full">{title}</span>
      {slug ? (
        <>
          <Dot />
          <span className="font-mono text-[13px] text-muted-foreground">{slug}</span>
        </>
      ) : null}
      {status ? (
        <>
          <Dot />
          <StatusPill status={status} />
        </>
      ) : null}
    </div>
  );
}

function EventRow({ ev, now, index }: { ev: LiveEvent; now: number; index: number }) {
  const d = describeEvent(ev, useSeesProviders());
  return (
    <li
      data-testid="event-row"
      data-event-type={ev.type}
      style={stagger(index)}
      className="enter lift-row flex items-center gap-3 rounded-lg border bg-secondary/50 px-3 py-2.5 hover:border-border-strong hover:bg-secondary"
    >
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-secondary text-muted-foreground">
        <Icon name={d.icon} size={18} />
      </span>
      <EventText title={d.title} slug={d.slug} status={d.status} />
      <time dateTime={ev.occurredAt} title={formatDateTime(ev.occurredAt)} className="tabular shrink-0 self-start font-mono text-xs text-subtle sm:self-center">
        {timeAgo(ev.occurredAt, now)}
      </time>
    </li>
  );
}
